import Sammy from "3pp/sammy-0.7.4";
import hashQuery from "flow/utils/hashQuery";
import uri from "flow/utils/uri";
import manifestManager from "flow/utils/manifestManager";
import config from "flow/config";
import breadcrumbTracker from "flow/breadcrumbTracker";
import eventSystem from "flow/events/eventSystem";
import cleanUrl from "flow/utils/cleanUrl";
import { Dictionary } from "./api/types";
import bind from "bind-decorator";
import type { Workspace } from "flow/viewModels/workspace";

function ignoreFormSubmission() {
    // Override this function so that Sammy doesn't mess with forms
    if (config.preventImplicitFormSubmission) {
        this._checkFormSubmission = function () {
            return false;
        };
    }
    return true;
}

function removeApp(fragment: string) {
    return fragment.replace(/^\/App\//, "/");
}

export class Router {
    sammy = Sammy(ignoreFormSubmission);
    isRedirecting = false;
    skipRouting = false;
    currentHash = "";
    lastHash = "";
    homeRegExp = /#\/Home/;
    appRegExp = /#\/App/;
    workingContextRegExp = /#\/(.+)/;
    allCatchingRegExp = /.*/;
    callFromActivateById = false;
    workspace: Workspace;

    breadcrumbs = breadcrumbTracker.breadcrumbs;
    currentBreadcrumbTitle = breadcrumbTracker.currentBreadcrumbTitle;

    init(workspace: Workspace) {
        cleanUrl();

        this.workspace = workspace;
        this.sammy.get(this.homeRegExp, () => {
            // Matches #/
            workspace.getMyFlow().publish(config.topics.navigationPanelItemSelected, config.componentIds.homeContext);

            const fragment = uri.getFragment();

            if (this.shouldPerformRouting()) {
                breadcrumbTracker.setHomeLevel(fragment);
                const viewContext = hashQuery.getViewContextWithoutSelectedContainer(fragment);
                const selectedContainerId = hashQuery.getSelectedContainerId(fragment);
                workspace.workingContext
                    .loadComponentById(config.componentIds.homeContext, viewContext, selectedContainerId)
                    .done(() => {
                        eventSystem.publish(config.topics.routeChanged, config.componentIds.homeContext);
                    })
                    .fail(this.revertUrl);
            } else {
                breadcrumbTracker.updateCurrentFragment(fragment);
            }
        });

        this.sammy.get(this.appRegExp, () => {
            // Matches #/App...
            const fragment = removeApp(uri.getFragment());

            if (this.shouldPerformRouting()) {
                const viewModelId = hashQuery.getViewModelId(fragment);
                const args = hashQuery.getViewContextWithoutSelectedContainer(fragment);

                eventSystem.publish(config.topics.routeWillChange, {
                    workingContextId: viewModelId,
                    viewContext: args
                });

                breadcrumbTracker.setFunctionLevel(fragment);

                workspace.workingContext
                    .loadComponentById(
                        config.componentIds.defaultWorkingContext,
                        {
                            id: viewModelId,
                            activateById: this.callFromActivateById,
                            args: args
                        },
                        undefined
                    )
                    .done(() => {
                        eventSystem.publish(config.topics.routeChanged, viewModelId);
                    })
                    .fail(this.revertUrl);
            } else {
                breadcrumbTracker.updateCurrentFragment(fragment);
            }
        });

        this.sammy.get(this.workingContextRegExp, () => {
            // Matches #/...
            const fragment = uri.getFragment();

            const workingContextId = hashQuery.getContextId(fragment);

            workspace.getMyFlow().publish(config.topics.navigationPanelItemSelected, workingContextId);

            if (this.shouldPerformRouting()) {
                const viewContext = hashQuery.getViewContextWithoutSelectedContainer(fragment);
                const selectedContainerId = hashQuery.getSelectedContainerId(fragment);

                eventSystem.publish(config.topics.routeWillChange, { workingContextId, viewContext });

                const workingContextManifest = manifestManager.getManifestWithId(workingContextId);
                if (workingContextManifest.resetsBreadcrumbs) {
                    //resetsBreadcrumbs property sets breadcrumb on level 1
                    breadcrumbTracker.setFunctionLevel(fragment);
                } else {
                    breadcrumbTracker.addContextLevel(fragment);
                }

                workspace.workingContext
                    .loadComponentById(workingContextId, viewContext, selectedContainerId)
                    .done(() => {
                        eventSystem.publish(config.topics.routeChanged, workingContextId);
                    })
                    .fail(this.revertUrl);
            } else {
                breadcrumbTracker.updateCurrentFragment(fragment);
            }
        });

        this.sammy.get(this.allCatchingRegExp, () => {
            // Matches anything
            if (this.shouldPerformRouting()) {
                workspace
                    .getMyFlow()
                    .publish(config.topics.navigationPanelItemSelected, config.componentIds.homeContext);
                this.sammy.setLocation("#/Home");
            }
        });

        this.sammy.before(this.allCatchingRegExp, () => {
            const fragment = uri.getFragment();
            const completeFragment = "#" + fragment;

            const isHomeContext = this.homeRegExp.test(completeFragment);
            const isFunctionContext = this.appRegExp.test(completeFragment);
            const isWorkingContext = this.workingContextRegExp.test(completeFragment);
            let manifest;

            if (isWorkingContext && !isFunctionContext && !isHomeContext) {
                const workingContextId = hashQuery.getContextId(fragment);
                manifest = manifestManager.getManifestWithId(workingContextId);

                if (manifest === undefined || manifest === null) {
                    workspace.getMyFlow().error("Missing manifest for working context: " + workingContextId);
                    workspace
                        .getMyFlow()
                        .showErrorDialog(
                            workspace.getMyFlow().translate("FlowHtml.Translations.FailedToLoadWorkingContext"),
                            workspace.getMyFlow().translate("FlowHtml.Translations.FailedToLoadWorkingContextText") +
                                ": " +
                                workingContextId
                        );

                    return false;
                }
            } else if (isFunctionContext) {
                const viewModelId = hashQuery.getViewModelId(fragment);
                manifest = manifestManager.getManifestWithId(viewModelId);

                if (manifest === undefined || manifest === null) {
                    workspace.getMyFlow().error("Missing manifest for function: " + viewModelId);
                    workspace
                        .getMyFlow()
                        .showErrorDialog(
                            workspace.getMyFlow().translate("FlowHtml.Translations.FailedToLoadFunctionContext"),
                            workspace.getMyFlow().translate("FlowHtml.Translations.FailedToLoadFunctionContextText") +
                                ": " +
                                viewModelId
                        );

                    return false;
                }
            }

            const canLeaveResult = this.skipRouting
                ? []
                : workspace.collectCanLeaveResults(hashQuery.tryGetContextId(fragment));

            if (!this.isRedirecting && !this.skipRouting && canLeaveResult.length > 0) {
                this.isRedirecting = true;

                canLeaveResult.forEach(r => {
                    r.component.myFlow.notifyInfo(r.result, r.component.manifest.name);
                });

                // Keep hash url the same in address bar
                this.sammy.setLocation(this.currentHash);
            } else {
                this.isRedirecting = false;
                this.lastHash = this.currentHash;
                this.currentHash = completeFragment;
            }
            // Cancel the route if this returns false
            return canLeaveResult.length === 0;
        });

        this.sammy.run();
    }

    isFunctionContext() {
        return this.appRegExp.test("#" + uri.getFragment());
    }

    shouldPerformRouting() {
        if (this.skipRouting) {
            this.skipRouting = false;
            return false;
        }
        return true;
    }

    @bind
    revertUrl() {
        this.skipRouting = true;
        this.sammy.setLocation(this.lastHash);
    }

    navigateToWorkingContext(viewModelId: string, viewContext: Dictionary, selectedContainerId: string) {
        const paramString = hashQuery.makeViewContextStringForWorkingContext(
            viewModelId,
            viewContext,
            selectedContainerId
        );
        this.sammy.setLocation(paramString);
    }

    navigateHome(viewContext: Dictionary, selectedContainerId: string) {
        const paramString = hashQuery.makeViewContextStringForWorkingContext(
            config.componentIds.homeContext,
            viewContext,
            selectedContainerId
        );
        this.sammy.setLocation(paramString);
    }

    navigateApp(viewModelId: string, viewContext: Dictionary) {
        const paramString = hashQuery.makeViewContextStringForDefaultWorkingContext(viewModelId, viewContext);
        this.callFromActivateById = true;
        this.sammy.setLocation(paramString);
        this.callFromActivateById = false;
    }

    performBackNavigation() {
        if (this.workspace.viewport() === undefined || this.workspace.viewport().selectedComponent() === undefined) {
            return true;
        }

        const eventData = {
            handled: false
        };

        const selectedComponent = this.workspace.viewport().selectedComponent();

        if (selectedComponent !== undefined && selectedComponent !== null) {
            selectedComponent.onBeforeNavigateBack(eventData);
        }

        return eventData.handled === false;
    }

    navigateBasedOnFragment(fragment: string) {
        const id = hashQuery.getContextId(fragment);
        const viewContext = hashQuery.getViewContextWithoutSelectedContainer(fragment);
        const selectedContainerId = hashQuery.getSelectedContainerId(fragment);

        if (manifestManager.isHome(id) || id === "") {
            this.navigateHome(viewContext, selectedContainerId);
        } else if (manifestManager.isComponent(id)) {
            this.navigateApp(id, viewContext);
        } else if (manifestManager.isWorkingContext(id)) {
            this.navigateToWorkingContext(id, viewContext, selectedContainerId);
        }
    }

    navigateBack() {
        if (!this.performBackNavigation()) {
            return;
        }
        const fragment = breadcrumbTracker.back();

        this.navigateBasedOnFragment(fragment);
    }

    replaceLocation(url: string, skipRouting: boolean) {
        try {
            if (skipRouting) {
                if (window.location.href === url) {
                    return;
                }

                this.skipRouting = true;
            }

            window.location.replace(url);
        } catch (e) {
            // Cancel will raise an exception in IE
        }
    }

    replaceFragment(fragment: string, skipRouting: boolean) {
        const newUrl = uri.replaceFragment(fragment);
        this.replaceLocation(newUrl, skipRouting);
    }

    reloadHome() {
        this.replaceLocation(uri.getBaseUrl(), false);
    }

    reload() {
        try {
            window.location.reload();
        } catch (e) {
            // Cancel will raise an exception in IE
        }
    }

    cleanHistory(id: string) {
        breadcrumbTracker.cleanHistory(id);
    }

    getIdFragment() {
        const fragment = uri.getFragment();

        let idFragment = hashQuery.getAppWithParameterViewModelId(fragment);

        idFragment = idFragment == "" ? config.componentIds.defaultWorkingContext : idFragment;

        return idFragment;
    }

    reactivateWorkingContext() {
        const fragment = removeApp(uri.getFragment());

        const workingContextId = hashQuery.tryGetContextId(fragment);

        if (workingContextId === "" || !manifestManager.isWorkingContext(workingContextId)) {
            return;
        }

        const viewContext = hashQuery.getViewContextWithoutSelectedContainer(fragment);
        const selectedContainerId = hashQuery.getSelectedContainerId(fragment);

        this.workspace.workingContext.loadComponentById(workingContextId, viewContext, selectedContainerId);
    }

    getCurrentFunctionViewContext() {
        const fragment = removeApp(uri.getFragment());
        return hashQuery.getViewContextWithoutSelectedContainer(fragment);
    }

    replaceViewContextAndThenRoute(workingContextId: string, viewContext: Dictionary, selectedContainerId: string) {
        router.replaceFragment(
            hashQuery.makeViewContextStringForWorkingContext(workingContextId, viewContext, selectedContainerId),
            false
        );
    }

    adjustViewContextInUrl(newViewContext: Dictionary) {
        const fragment = removeApp(uri.getFragment());
        const workingContextId = hashQuery.getViewModelId(fragment);
        let selectedContainerId = hashQuery.getSelectedContainerId(fragment);

        // Avoid losing _containerId if updated by view model code
        if (newViewContext && newViewContext.hasOwnProperty(config.selectedContainerPropertyName)) {
            selectedContainerId = newViewContext[config.selectedContainerPropertyName];
        }

        const newFragment = this.isFunctionContext()
            ? hashQuery.makeViewContextStringForDefaultWorkingContext(workingContextId, newViewContext)
            : hashQuery.makeViewContextStringForWorkingContext(workingContextId, newViewContext, selectedContainerId);

        this.replaceFragment(newFragment, true);
    }

    updateSelectedContainerInUrl(selectedContainerId: string) {
        const fragment = removeApp(uri.getFragment());

        const workingContextId = hashQuery.getViewModelId(fragment);
        const viewContext = hashQuery.getViewContextWithoutSelectedContainer(fragment);

        const newFragment = this.isFunctionContext()
            ? hashQuery.makeViewContextStringForDefaultWorkingContext(workingContextId, viewContext)
            : hashQuery.makeViewContextStringForWorkingContext(workingContextId, viewContext, selectedContainerId);

        this.replaceFragment(newFragment, true);
    }
}

const router = new Router();
export default router;
